import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from "react-markdown";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import toast from 'react-hot-toast';
import serverURL from '../config';

// Helper function to convert YouTube URLs to embed format
const getEmbedUrl = (url) => {
  if (!url) return url;
  const youtubeRegex = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/;
  const match = url.match(youtubeRegex);
  if (match?.[1]) {
    return `https://www.youtube.com/embed/${match[1]}`;
  }
  return url;
};

const buildHtmlDocument = (content) => `<!DOCTYPE html><html><head><style>body { font-family: ui-sans-serif, system-ui, -apple-system, sans-serif; line-height: 1.6; color: #374151; padding: 1rem; margin: 0; word-wrap: break-word; } img { max-width: 100%; height: auto; }</style></head><body>${content}</body></html>`;

// Extracts content type badge class
const getContentTypeBadgeClass = (contentType) => {
  if (contentType === 'video') return 'bg-red-100 text-red-700';
  if (contentType === 'markdown') return 'bg-purple-100 text-purple-700';
  if (contentType === 'html') return 'bg-orange-100 text-orange-700';
  return 'bg-blue-100 text-blue-700';
};

const LessonContent = ({ eventId, levelId }) => {
  const [lessons, setLessons] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newLesson, setNewLesson] = useState({
    title: '',
    description: '',
    contentType: 'text', // text, html, markdown, video, pdf, iframe
    content: '',
    isOptional: false
  });
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [editingLesson, setEditingLesson] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load lessons from API
  const loadLessons = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${serverURL}/lesson-content/level/${levelId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLessons(response.data || []);
    } catch (error) {
      console.error('Failed to load lessons:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to load lessons');
      }
      setLessons([]);
    } finally {
      setLoading(false);
    }
  }, [levelId]);

  useEffect(() => {
    if (levelId) {
      loadLessons();
    }
  }, [levelId, loadLessons]);

  const saveLessons = async (updatedLessons) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${serverURL}/lesson-content/level/${levelId}`,
        { lessons: updatedLessons },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLessons(updatedLessons);
      toast.success('Lessons saved successfully!');
    } catch (error) {
      console.error('Failed to save lessons:', error);
      toast.error('Failed to save lessons');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleAddLesson = async () => {
    if (!newLesson.title || !newLesson.content) {
      toast('Please fill in title and content');
      return;
    }

    const lesson = {
      id: Date.now(),
      ...newLesson
    };

    try {
      await saveLessons([...lessons, lesson]);
      setNewLesson({ title: '', description: '', contentType: 'text', content: '', isOptional: false });
      setIsModalOpen(false);
    } catch {
      // Error already handled in saveLessons
    }
  };


  const handleSelectLesson = (lesson) => {
    setSelectedLesson(lesson);
  };

  const handleEdit = (lesson) => {
    setEditingLesson(lesson);
    setNewLesson(lesson);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this lesson?')) {
      try {
        await saveLessons(lessons.filter(lesson => lesson.id !== id));
        if (selectedLesson?.id === id) {
          setSelectedLesson(null);
        }
      } catch {
        // Error already handled in saveLessons
      }
    }
  };

  const handleUpdateLesson = async () => {
    if (!newLesson.title || !newLesson.content) {
      toast('Please fill in title and content');
      return;
    }

    const updated = lessons.map(lesson =>
      lesson.id === editingLesson.id
        ? { ...newLesson, id: lesson.id }
        : lesson
    );

    try {
      await saveLessons(updated);
      setEditingLesson(null);
      setNewLesson({ title: '', description: '', contentType: 'text', content: '', isOptional: false });
      setIsModalOpen(false);
    } catch {
      // Error already handled in saveLessons
    }
  };

  const handleSaveLesson = () => {
    if (editingLesson) {
      handleUpdateLesson();
    } else {
      handleAddLesson();
    }
  };

  const renderContentPreview = () => {
    switch (selectedLesson?.contentType) {
      case 'markdown':
        return <ReactMarkdown>{selectedLesson.content}</ReactMarkdown>;
      case 'html':
        return (
          <iframe
            title="lesson-preview"
            className="w-full h-96 bg-white border rounded"
            srcDoc={buildHtmlDocument(selectedLesson.content)}
            sandbox=""
            frameBorder="0"
          />
        );
      case 'video':
        return (
          <video controls className="w-full" src={selectedLesson.content}>
            <track kind="captions" srcLang="en" label="English captions" />
          </video>
        );
      case 'iframe':
        return (
          <iframe
            title="lesson-content"
            className="w-full h-96"
            src={getEmbedUrl(selectedLesson.content)}
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-presentation"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
            allowFullScreen
            frameBorder="0"
          />
        );
      case 'pdf':
        return <iframe title="lesson-content" className="w-full h-96" src={selectedLesson.content} sandbox="allow-same-origin allow-scripts" />;
      default:
        return <p className="whitespace-pre-wrap">{selectedLesson?.content}</p>;
    }
  };

  if (loading) {
    return (
      <div className="p-5 flex items-center justify-center h-48">
        <p className="text-sm text-campus-text-body animate-pulse">Loading lessons…</p>
      </div>
    );
  }


  return (
    <div className="p-5">

      {/* ── Header ───────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h2 className="text-base font-heading font-bold text-campus-text-primary">Lessons</h2>
          <p className="text-xs text-campus-text-body mt-0.5">{lessons.length} lesson{lessons.length !== 1 ? 's' : ''} configured</p>
        </div>
        <button
          onClick={() => {
            setEditingLesson(null);
            setNewLesson({ title: '', description: '', contentType: 'text', content: '', isOptional: false });
            setIsModalOpen(true);
          }}
          className="inline-flex items-center gap-2 bg-campus-primary hover:bg-campus-primary-dark text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors shadow-sm whitespace-nowrap"
        >
          <FontAwesomeIcon icon={faPlus} className="text-xs" />
          Add Lesson
        </button>
      </div>

      {/* ── Two-panel layout ─────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Lesson list */}
        <div className="bg-campus-surface border border-campus-border rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-campus-border">
            <p className="text-xs font-semibold text-campus-text-body uppercase tracking-wide">Lesson List</p>
          </div>
          <div className="max-h-96 overflow-y-auto divide-y divide-campus-border">
            {lessons.length === 0 ? (
              <div className="p-6 text-center">
                <p className="text-sm text-campus-text-body">No lessons yet. Add your first lesson above.</p>
              </div>
            ) : (
              lessons.map(lesson => (
                <button
                  key={lesson.id}
                  type="button"
                  className={`w-full text-left px-4 py-3 hover:bg-campus-bg transition-colors duration-150 border-l-4 ${selectedLesson?.id === lesson.id
                    ? 'border-l-campus-primary bg-campus-bg'
                    : 'border-l-transparent'
                    }`}
                  onClick={() => handleSelectLesson(lesson)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-campus-text-primary truncate">{lesson.title}</p>
                      {lesson.description && (
                        <p className="text-xs text-campus-text-body mt-0.5 truncate">{lesson.description}</p>
                      )}
                      <span className={`inline-block mt-1.5 text-xs font-medium px-2 py-0.5 rounded-full ${getContentTypeBadgeClass(lesson.contentType)}`}>
                        {lesson.contentType}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleEdit(lesson); }}
                        className="p-1.5 rounded-lg hover:bg-campus-surface text-campus-text-body hover:text-campus-primary transition-colors"
                        title="Edit"
                      >
                        <FontAwesomeIcon icon={faEdit} className="text-xs" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleDelete(lesson.id); }}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-campus-text-body hover:text-campus-error transition-colors"
                        title="Delete"
                      >
                        <FontAwesomeIcon icon={faTrash} className="text-xs" />
                      </button>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Preview panel */}
        <div>
          {selectedLesson ? (
            <div className="bg-campus-surface border border-campus-border rounded-xl h-96 overflow-hidden flex flex-col">
              <div className="px-4 py-3 border-b border-campus-border flex items-center justify-between">
                <p className="text-sm font-semibold text-campus-text-primary truncate">{selectedLesson.title}</p>

              </div>
              <div className="flex-grow overflow-y-auto p-4 text-sm text-campus-text-primary">
                {renderContentPreview()}
              </div>
            </div>
          ) : (
            <div className="bg-campus-bg border border-campus-border rounded-xl h-96 flex items-center justify-center">
              <p className="text-sm text-campus-text-body">Select a lesson to preview</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Add / Edit Modal ───────────────────────────────────── */}
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-campus-surface rounded-2xl shadow-hover w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col">
            <div className="p-5 border-b border-campus-border">
              <h3 className="text-base font-heading font-bold text-campus-text-primary">
                {editingLesson ? 'Edit Lesson' : 'Add Lesson'}
              </h3>
            </div>
            <div className="overflow-y-auto p-5 space-y-4 flex-1">
              {/* Title */}
              <div>
                <label htmlFor="lesson-title" className="block text-xs font-semibold text-campus-text-body uppercase tracking-wide mb-1.5">Title *</label>
                <input
                  id="lesson-title"
                  type="text"
                  value={newLesson.title}
                  onChange={(e) => setNewLesson({ ...newLesson, title: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-campus-border rounded-lg bg-campus-surface text-campus-text-primary focus:outline-none focus:ring-2 focus:ring-campus-primary transition-all"
                  placeholder="Enter lesson title"
                />
              </div>
              {/* Description */}
              <div>
                <label htmlFor="lesson-description" className="block text-xs font-semibold text-campus-text-body uppercase tracking-wide mb-1.5">Description</label>
                <textarea
                  id="lesson-description"
                  value={newLesson.description}
                  onChange={(e) => setNewLesson({ ...newLesson, description: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-campus-border rounded-lg bg-campus-surface text-campus-text-primary focus:outline-none focus:ring-2 focus:ring-campus-primary transition-all resize-none h-16"
                  placeholder="Enter lesson description"
                />
              </div>
              {/* Content Type */}
              <div>
                <label htmlFor="lesson-content-type" className="block text-xs font-semibold text-campus-text-body uppercase tracking-wide mb-1.5">Content Type</label>
                <select
                  id="lesson-content-type"
                  value={newLesson.contentType}
                  onChange={(e) => setNewLesson({ ...newLesson, contentType: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-campus-border rounded-lg bg-campus-surface text-campus-text-primary focus:outline-none focus:ring-2 focus:ring-campus-primary transition-all"
                >
                  <option value="text">Plain Text</option>
                  <option value="html">HTML</option>
                  <option value="markdown">Markdown</option>
                  <option value="video">Video URL</option>
                  <option value="pdf">PDF URL</option>
                  <option value="iframe">iFrame URL</option>
                </select>
              </div>
              {/* Content */}
              <div>
                <label htmlFor="lesson-content" className="block text-xs font-semibold text-campus-text-body uppercase tracking-wide mb-1.5">Content *</label>
                <textarea
                  id="lesson-content"
                  value={newLesson.content}
                  onChange={(e) => setNewLesson({ ...newLesson, content: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-campus-border rounded-lg bg-campus-surface text-campus-text-primary focus:outline-none focus:ring-2 focus:ring-campus-primary transition-all resize-none h-24"
                  placeholder={`Enter content for ${newLesson.contentType}`}
                />
              </div>
              {/* Optional */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newLesson.isOptional}
                  onChange={(e) => setNewLesson({ ...newLesson, isOptional: e.target.checked })}
                  className="w-4 h-4 rounded border-campus-border text-campus-primary focus:ring-campus-primary"
                />
                <span className="text-sm text-campus-text-body">Optional lesson</span>
              </label>
            </div>
            <div className="p-5 border-t border-campus-border flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setIsModalOpen(false); setEditingLesson(null); setNewLesson({ title: '', description: '', contentType: 'text', content: '', isOptional: false }); }}
                className="px-4 py-2 text-sm border border-campus-border text-campus-text-body rounded-lg hover:bg-campus-bg transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveLesson}
                className="px-4 py-2 text-sm bg-campus-primary hover:bg-campus-primary-dark text-white rounded-lg transition-colors font-semibold"
              >
                {editingLesson ? 'Update' : 'Add'} Lesson
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Preview Modal ───────────────────────────────────────── */}
      {isPreviewModalOpen && selectedLesson && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-campus-surface rounded-2xl shadow-hover w-full max-w-4xl mx-4 max-h-[85vh] flex flex-col">
            <div className="p-5 border-b border-campus-border flex items-center justify-between">
              <h3 className="text-base font-heading font-bold text-campus-text-primary">{selectedLesson.title}</h3>
              <button
                type="button"
                onClick={() => setIsPreviewModalOpen(false)}
                className="p-1.5 rounded-lg text-campus-text-body hover:bg-campus-bg transition-colors text-lg leading-none"
              >
                &times;
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {renderContentPreview()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

LessonContent.propTypes = {
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  levelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default LessonContent;
